import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api/client.js'
import type { Dashboard as DashboardData, Task } from '../api/types.js'
import { Icon } from '../components/icons.js'
import { useToast } from '../components/toast.js'
import { EmptyState, Notice, Tile } from '../components/ui.js'
import { addDays, formatDate, formatDateLong, formatWeekday, relativeDayLabel, todayIso } from '../lib/format.js'
import { PageHead } from './Layout.js'
import { SetupChecklist } from './SetupChecklist.js'
import { TaskModal } from './TaskDialogs.js'
import { assigneeLabel } from '../components/people.js'

/**
 * The morning screen. It answers four questions about the work, in this
 * order: what is due today, what has nobody to do it, what is still waiting
 * for a decision, and what needs to go out to billing. People appear only as
 * capacity for the coming week, not as a scoreboard.
 */
export function Dashboard() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [openTask, setOpenTask] = useState<string | null>(null)
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api<DashboardData>('/api/dashboard'),
    refetchInterval: 60_000,
  })

  const today = data?.today ?? todayIso()
  const forecastTo = addDays(today, 6)
  const forecast = useQuery({
    queryKey: ['occurrences', `forecast-${today}`],
    queryFn: () => api<{ tasks: Task[] }>(`/api/occurrences?from=${today}&to=${forecastTo}`),
    enabled: Boolean(data),
  })

  if (isLoading) return <DashboardSkeleton />
  if (error || !data) return <Notice kind="error">Die Übersicht konnte nicht geladen werden.</Notice>

  const quotaBase = data.month.done + data.month.missed
  const quota = quotaBase > 0 ? Math.round((data.month.done / quotaBase) * 100) : null
  const handoverOpen = data.handover.youtillyPending + data.handover.youtillyFailed

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    await queryClient.invalidateQueries({ queryKey: ['occurrences'] })
  }

  return (
    <>
      <PageHead title="Übersicht" subtitle={formatDateLong(data.today)} />

      <SetupChecklist />

      <div className="tiles" style={{ marginBottom: 22 }}>
        <Tile
          label="Heute"
          value={`${data.todayCounts.done} / ${data.todayCounts.total}`}
          sub={data.todayCounts.open > 0 ? `${data.todayCounts.open} noch offen` : data.todayCounts.total > 0 ? 'alles erledigt' : 'nichts geplant'}
          tone={data.todayCounts.total > 0 && data.todayCounts.open === 0 ? 'good' : undefined}
        />
        <Tile
          label="Ohne Zuständigen"
          value={data.unassignedCount}
          sub={data.unassignedCount === 0 ? 'nächste 7 Tage sind verteilt' : 'in den nächsten 7 Tagen'}
          tone={data.unassignedCount > 0 ? 'alert' : 'good'}
        />
        <Tile
          label="Wartet auf Entscheidung"
          value={data.overdue}
          sub={data.overdue === 0 ? 'keine Rückstände' : 'nicht erledigt, noch nicht geschlossen'}
          tone={data.overdue > 0 ? 'alert' : 'good'}
        />
        <Tile
          label="Offene Mängel"
          value={data.openIssues}
          sub={<Link to="/maengel">zur Mängelliste</Link>}
          tone={data.openIssues > 0 ? 'alert' : undefined}
        />
        <Tile
          label="Übergabe"
          value={handoverOpen}
          sub={
            <Link to="/uebergabe">
              {data.handover.youtillyFailed > 0
                ? `${data.handover.youtillyFailed} Youtilly-Fehler, ${data.handover.unexported} nicht exportiert`
                : `an Youtilly zu melden, ${data.handover.unexported} nicht exportiert`}
            </Link>
          }
          tone={data.handover.youtillyFailed > 0 ? 'alert' : undefined}
        />
      </div>

      <Forecast today={today} tasks={forecast.data?.tasks ?? []} loading={forecast.isLoading} />

      <div style={{ display: 'grid', gap: 18, gridTemplateColumns: 'repeat(auto-fit, minmax(min(340px, 100%), 1fr))' }}>
        <section className="card">
          <div className="card-head">
            <h2>
              <Icon name="users" size={16} /> Ohne Zuständigen
            </h2>
            <Link className="btn secondary small" to={`/aufgaben?hinweis=nicht_zugewiesen&von=${today}&bis=${addDays(today, 6)}`}>
              In der Liste
            </Link>
          </div>
          {data.unassigned.length === 0 ? (
            <EmptyState title="Alles verteilt" hint="Jede Aufgabe der nächsten sieben Tage hat jemanden." />
          ) : (
            <ul className="day-list">
              {data.unassigned.map((task) => (
                <li key={task.id}>
                  <button type="button" className="open" onClick={() => setOpenTask(task.id)}>
                    <div className="t">{task.title}</div>
                    <div className="m">
                      {task.siteName}
                      {task.path.length > 0 ? `, ${task.path.join(' › ')}` : ''}
                    </div>
                    <div className="m">
                      {relativeDayLabel(task.dueDate, today)}
                      {task.dueTime ? `, bis ${task.dueTime} Uhr` : ''}
                    </div>
                  </button>
                </li>
              ))}
              {data.unassignedCount > data.unassigned.length && (
                <li className="muted small" style={{ padding: '8px 14px' }}>
                  und {data.unassignedCount - data.unassigned.length} weitere in der Aufgabenliste
                </li>
              )}
            </ul>
          )}
        </section>

        <section className="card">
          <div className="card-head">
            <h2>
              <Icon name="clock" size={16} /> Wartet auf Entscheidung
            </h2>
            <span className="muted small">Sofort erledigen, endgültig schließen oder entfallen lassen</span>
          </div>
          {data.decisions.length === 0 ? (
            <EmptyState title="Keine offenen Rückstände" hint="Alle fälligen Aufgaben wurden erledigt oder entschieden." />
          ) : (
            <ul className="day-list">
              {data.decisions.map((task) => (
                <li key={task.id}>
                  <button type="button" className="missed" onClick={() => setOpenTask(task.id)}>
                    <div className="t">{task.title}</div>
                    <div className="m">
                      {task.siteName}
                      {task.path.length > 0 ? `, ${task.path.join(' › ')}` : ''}
                    </div>
                    <div className="m">
                      {formatDate(task.dueDate)}
                      {assigneeLabel(task) ? `, ${assigneeLabel(task)}` : ', niemand zugewiesen'}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="card">
          <div className="card-head">
            <h2>
              <Icon name="repeat" size={16} /> Auslastung nächste 7 Tage
            </h2>
            <Link className="btn secondary small" to="/mitarbeiter">
              Mitarbeiter
            </Link>
          </div>
          <div className="card-body stack" style={{ gap: 10 }}>
            {data.staffAhead.length === 0 ? (
              <EmptyState title="Noch keine Mitarbeiter" hint="Legen Sie Mitarbeiter an und laden Sie sie ein." />
            ) : (
              data.staffAhead.map((person) => {
                const peak = Math.max(1, ...data.staffAhead.map((p) => p.open))
                return (
                  <div key={person.id} className="load-row">
                    <Link to={`/mitarbeiter/${person.id}`} className="strong">
                      {person.name}
                    </Link>
                    <div className="load-bar" aria-hidden="true">
                      <i style={{ width: `${Math.round((person.open / peak) * 100)}%` }} />
                    </div>
                    <span className="muted small nowrap">
                      {person.open === 0 ? 'nichts geplant' : `${person.open} offen`}
                      {person.today > 0 ? `, heute ${person.today}` : ''}
                    </span>
                  </div>
                )
              })
            )}
          </div>
        </section>

        <section className="card">
          <div className="card-head">
            <h2>
              <Icon name="document" size={16} /> Erfüllung letzte 30 Tage
            </h2>
            <Link className="btn secondary small" to="/nachweise">
              Nachweise
            </Link>
          </div>
          <div className="card-body">
            <div style={{ fontSize: 34, fontWeight: 700, lineHeight: 1 }} className={quota !== null && quota >= 95 ? 'ok-text' : undefined}>
              {quota === null ? 'Noch keine fälligen Aufgaben' : `${quota} %`}
            </div>
            <div className="muted small" style={{ marginTop: 6 }}>
              {data.month.done} erledigt, {data.month.missed} nicht erledigt. Das ist die Zahl, die im Nachweis steht.
            </div>
          </div>
        </section>
      </div>

      <TaskModal
        taskId={openTask}
        onClose={() => setOpenTask(null)}
        onChanged={async (message) => {
          if (message) toast(message)
          setOpenTask(null)
          await refresh()
        }}
      />
    </>
  )
}

/**
 * The week ahead at a glance, so the Verwalter can spot a heavy day, or an
 * empty one, before it arrives rather than on the morning itself.
 */
function Forecast({ today, tasks, loading }: { today: string; tasks: Task[]; loading: boolean }) {
  const days = Array.from({ length: 7 }, (_, index) => addDays(today, index))
  const byDay = new Map<string, Task[]>(days.map((day) => [day, []]))
  for (const task of tasks) byDay.get(task.dueDate)?.push(task)

  const peak = Math.max(1, ...days.map((day) => byDay.get(day)?.length ?? 0))

  return (
    <section className="card" style={{ marginBottom: 22 }}>
      <div className="card-head">
        <h2>
          <Icon name="calendar" size={16} /> Nächste 7 Tage
        </h2>
        <Link className="btn secondary small" to={`/aufgaben?von=${today}&bis=${addDays(today, 6)}`}>
          Aufgabenliste
        </Link>
      </div>
      <div className="card-body">
        {loading ? (
          <div className="skeleton" style={{ height: 96 }} />
        ) : (
          <div className="forecast">
            {days.map((day) => {
              const dayTasks = byDay.get(day) ?? []
              const done = dayTasks.filter((task) => task.status === 'done').length
              const unassigned = dayTasks.filter((task) => !assigneeLabel(task) && task.status === 'open').length
              return (
                <Link className={`forecast-day ${day === today ? 'today' : ''}`} to={`/aufgaben?von=${day}&bis=${day}`} key={day}>
                  <span className="wd">{formatWeekday(day)}</span>
                  <span className="dn">{Number(day.slice(8))}</span>
                  <span className="col" style={{ height: `${Math.round((dayTasks.length / peak) * 46) + 4}px` }}>
                    <i style={{ height: `${dayTasks.length > 0 ? (done / dayTasks.length) * 100 : 0}%` }} />
                  </span>
                  <span className="n">{dayTasks.length === 0 ? 'frei' : dayTasks.length}</span>
                  {unassigned > 0 && (
                    <span className="badge warn" title={`${unassigned} ohne Zuständigen`}>
                      {unassigned} offen
                    </span>
                  )}
                </Link>
              )
            })}
          </div>
        )}
      </div>
    </section>
  )
}

/** Keeps the page's shape while it loads, instead of a bare line of text. */
function DashboardSkeleton() {
  return (
    <>
      <PageHead title="Übersicht" />
      <div className="tiles" style={{ marginBottom: 22 }} aria-hidden="true">
        {Array.from({ length: 5 }, (_, index) => (
          <div className="skeleton" style={{ height: 104 }} key={index} />
        ))}
      </div>
      <div className="skeleton" style={{ height: 150, marginBottom: 22 }} aria-hidden="true" />
      <div style={{ display: 'grid', gap: 18, gridTemplateColumns: 'repeat(auto-fit, minmax(min(340px, 100%), 1fr))' }} aria-hidden="true">
        <div className="skeleton" style={{ height: 260 }} />
        <div className="skeleton" style={{ height: 260 }} />
      </div>
      <span className="muted small" role="status">
        Übersicht wird geladen …
      </span>
    </>
  )
}
